import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Pause, Play, Quote } from "lucide-react";
import { useRef } from "react";
import { toast } from "sonner";
import { EXCERPTS } from "../content";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Excerpts = () => {
  const [active, setActive] = useState(EXCERPTS[0].id);
  const [playing, setPlaying] = useState(null);
  const [loadingClip, setLoadingClip] = useState(null);
  const audioRef = useRef(null);
  const current = EXCERPTS.find((e) => e.id === active);

  const stopAudio = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(null);
  };

  const toggleClip = (clipId) => {
    if (playing === clipId) {
      stopAudio();
      return;
    }
    stopAudio();
    setLoadingClip(clipId);
    const audio = new Audio(`${API}/audio/${clipId}`);
    audioRef.current = audio;
    audio.onended = () => setPlaying(null);
    audio.oncanplaythrough = () => setLoadingClip(null);
    audio.onerror = () => {
      setLoadingClip(null);
      setPlaying(null);
      toast.error("This narration is still being recorded — try again in a moment.");
    };
    audio
      .play()
      .then(() => setPlaying(clipId))
      .catch(() => setLoadingClip(null));
  };

  const switchTab = (id) => {
    stopAudio();
    setActive(id);
  };

  return (
    <section id="excerpts" className="py-20 lg:py-32 relative" data-testid="excerpts-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-alt text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Clips from the codex</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F8FAFC] max-w-2xl leading-tight">
            Read the passages readers can't stop quoting
          </h2>
          <p className="mt-3 text-sm text-[#94A3B8] flex items-center gap-2">
            <Play className="w-3.5 h-3.5 text-[#D4AF37]" /> Tap the play button on any passage to hear it narrated.
          </p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="flex flex-wrap gap-3" role="tablist" aria-label="Chapters">
            {EXCERPTS.map((e) => (
              <button
                key={e.id}
                role="tab"
                aria-selected={active === e.id}
                data-testid={`excerpt-tab-${e.id}`}
                onClick={() => switchTab(e.id)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                  active === e.id
                    ? "bg-[#D4AF37] text-[#052730] gold-glow"
                    : "border border-[#D4AF37]/25 text-[#94A3B8] hover:text-[#FCD34D] hover:border-[#D4AF37]/50"
                }`}
              >
                {e.tab}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid lg:grid-cols-12 gap-8"
            data-testid="excerpt-panel"
          >
            <div className="lg:col-span-8 space-y-6">
              {current.quotes.map((q, i) => {
                const clipId = `${current.id}-${i}`;
                const isPlaying = playing === clipId;
                const isLoading = loadingClip === clipId;
                return (
                  <motion.blockquote
                    key={q.text}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: i * 0.12 }}
                    className="relative rounded-lg border border-[#D4AF37]/15 bg-[#0A404E] p-8 sm:p-10"
                    data-testid={`excerpt-quote-${i}`}
                  >
                    <Quote className="absolute top-6 left-6 w-6 h-6 text-[#D4AF37]/40" aria-hidden="true" />
                    <p className="font-serif-alt italic text-xl sm:text-2xl lg:text-3xl text-[#F8FAFC] leading-relaxed pl-8">
                      {q.text}
                    </p>
                    <footer className="mt-4 pl-8 flex flex-wrap items-center gap-4">
                      <span className="font-mono-alt text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]/70">
                        {current.chapter} · {q.page}
                      </span>
                      <button
                        data-testid={`excerpt-play-${clipId}`}
                        onClick={() => toggleClip(clipId)}
                        aria-label={isPlaying ? "Pause narration" : "Play narration"}
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[11px] font-mono-alt uppercase tracking-[0.2em] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] ${
                          isPlaying
                            ? "bg-[#D4AF37] text-[#052730] border-[#D4AF37]"
                            : "border-[#D4AF37]/30 text-[#FCD34D] hover:bg-[#D4AF37]/10"
                        }`}
                      >
                        {isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : isPlaying ? (
                          <Pause className="w-3.5 h-3.5" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                        {isPlaying ? "Playing" : "Listen"}
                      </button>
                    </footer>
                  </motion.blockquote>
                );
              })}
            </div>
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="rounded-lg border border-[#D4AF37]/25 bg-gradient-to-b from-[#0A404E] to-[#06333E] p-8 h-full flex flex-col justify-between"
              >
                <div>
                  <p className="font-mono-alt text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">
                    {current.chapter} — begins {current.page}
                  </p>
                  <h3 className="mt-3 font-display text-2xl font-bold text-[#F8FAFC]">{current.tab}</h3>
                  <p className="mt-4 text-[#94A3B8] leading-relaxed text-sm sm:text-base">{current.note}</p>
                </div>
                <p className="mt-8 font-serif-alt italic text-amber-200/70 text-sm">
                  …and {current.id === "menu" ? "36" : "33"} more pages like this.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Excerpts;
